/** EDP-00007 — Adaptive Experience framework (mirrors pkg/adaptivexp) */

export const EDP_AX_ID = 'EDP-00007';

export type Philosophy =
  | 'one_workflow'
  | 'many_devices'
  | 'task_continuity'
  | 'context_preservation'
  | 'device_awareness'
  | 'accessibility_first'
  | 'performance_first'
  | 'ai_everywhere'
  | 'consistency_always';

export type DeviceType =
  | 'ultra_wide'
  | 'desktop'
  | 'laptop'
  | 'tablet_landscape'
  | 'tablet_portrait'
  | 'large_mobile'
  | 'standard_mobile'
  | 'foldable';

export interface DeviceLayoutSpec {
  device: DeviceType;
  label: string;
  min_width_px: number;
  max_width_px?: number;
  explorer_visible: boolean;
  ai_panel_visible: boolean;
  timeline_visible: boolean;
  nav_mode?: string;
  touch_optimized?: boolean;
}

export const PHILOSOPHIES: { id: Philosophy; label: string; description: string }[] = [
  { id: 'one_workflow', label: 'One Workflow', description: 'Same business process on every device' },
  { id: 'many_devices', label: 'Many Devices', description: 'Optimize presentation per form factor' },
  { id: 'task_continuity', label: 'Task Continuity', description: 'Resume in-progress work across devices' },
  { id: 'context_preservation', label: 'Context Preservation', description: 'Scope, filters, selection travel with user' },
  { id: 'device_awareness', label: 'Device Awareness', description: 'Layout adapts to input method and screen' },
  { id: 'accessibility_first', label: 'Accessibility First', description: 'WCAG 2.2 AA minimum; never regress' },
  { id: 'performance_first', label: 'Performance First', description: 'Skeletons, lazy load, perceived speed' },
  { id: 'ai_everywhere', label: 'AI Everywhere', description: 'Copilot on all supported devices' },
  { id: 'consistency_always', label: 'Consistency Always', description: 'Same tokens and interaction semantics' },
];

export const DEVICE_LAYOUTS: DeviceLayoutSpec[] = [
  { device: 'ultra_wide', label: 'Ultra-wide', min_width_px: 1920, explorer_visible: true, ai_panel_visible: true, timeline_visible: true, nav_mode: 'sidebar' },
  { device: 'desktop', label: 'Desktop', min_width_px: 1280, max_width_px: 1919, explorer_visible: true, ai_panel_visible: true, timeline_visible: true, nav_mode: 'sidebar' },
  { device: 'laptop', label: 'Laptop', min_width_px: 1024, max_width_px: 1279, explorer_visible: true, ai_panel_visible: true, timeline_visible: false, nav_mode: 'sidebar_collapsible' },
  { device: 'tablet_landscape', label: 'Tablet Landscape', min_width_px: 768, max_width_px: 1023, explorer_visible: false, ai_panel_visible: true, timeline_visible: false, nav_mode: 'drawer', touch_optimized: true },
  { device: 'tablet_portrait', label: 'Tablet Portrait', min_width_px: 600, max_width_px: 767, explorer_visible: false, ai_panel_visible: false, timeline_visible: false, nav_mode: 'drawer', touch_optimized: true },
  { device: 'large_mobile', label: 'Large Mobile', min_width_px: 414, max_width_px: 599, explorer_visible: false, ai_panel_visible: false, timeline_visible: false, nav_mode: 'bottom_tabs', touch_optimized: true },
  { device: 'standard_mobile', label: 'Standard Mobile', min_width_px: 320, max_width_px: 413, explorer_visible: false, ai_panel_visible: false, timeline_visible: false, nav_mode: 'bottom_tabs', touch_optimized: true },
];

export const CONTINUITY_FIELDS = [
  'Current Context', 'Open Objects', 'Selected Items', 'Filters', 'Sort Order',
  'Search State', 'Unsaved Drafts', 'AI Conversation', 'Workspace Layout',
];

export const ACCESSIBILITY_STANDARDS = [
  'WCAG 2.2 AA', 'Keyboard Navigation', 'Logical Focus Order', 'Screen Reader Compatibility',
  'ARIA Standards', 'High Contrast', 'Reduced Motion', 'Scalable Typography', 'Color-independent Status',
];

export const DENSITY_MODES = ['Compact', 'Comfortable', 'Spacious'] as const;
export type DensityMode = typeof DENSITY_MODES[number];

export const NOTIFICATION_CHANNELS = ['In-App', 'Push', 'Email', 'Digest'];

export const PERSONALIZATION_OPTIONS = [
  'Pinned Modules', 'Favorite Workspaces', 'Saved Layouts', 'Dashboard Widgets',
  'Theme', 'Density', 'Language', 'Timezone', 'Notification Preferences',
  'Accessibility Preferences', 'Role-based Defaults',
];

export const MOBILE_WORKFLOWS = [
  'Executive Review', 'Approvals', 'Notifications', 'AI Chat', 'Quick Search',
  'Task Completion', 'Monitoring', 'Emergency Operations', 'Quick Insights',
];

export const TOUCH_TARGET_MIN_PX = 44;

export function resolveDeviceLayout(widthPx: number): DeviceLayoutSpec {
  for (let i = DEVICE_LAYOUTS.length - 1; i >= 0; i--) {
    const s = DEVICE_LAYOUTS[i];
    if (widthPx >= s.min_width_px && (s.max_width_px == null || widthPx <= s.max_width_px)) {
      return s;
    }
  }
  return DEVICE_LAYOUTS[DEVICE_LAYOUTS.length - 1];
}

export function buildAdaptiveDashboard() {
  return {
    philosophies: PHILOSOPHIES.length,
    device_types: DEVICE_LAYOUTS.length,
    continuity_fields: CONTINUITY_FIELDS.length,
    accessibility_standards: ACCESSIBILITY_STANDARDS.length,
    notification_channels: NOTIFICATION_CHANNELS.length,
    personalization_options: PERSONALIZATION_OPTIONS.length,
  };
}
